import { Box, styled, Typography } from "@material-ui/core";
import type { ReactNode } from "react";
import { CREDENTIALING_THRESHOLDS } from "./credentialing-config";
import { EXAM_COLORS } from "./epocus-exam-colors";
import { EXAM_ICONS, EXAM_LABELS } from "./epocus-exam-constants";
import { UltrasoundScanner } from "./epocus-icons";
import {
  EPOCUS_BG,
  EPOCUS_BORDER,
  EPOCUS_ICON_ACTIVE,
  EPOCUS_STATUS_REVIEWED_BG,
  EPOCUS_STATUS_REVIEWED_TEXT,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "./epocus-palette";
import { epocusTheme } from "./epocus-theme-provider";
import { getScoreColor, SCORE_GOOD, SCORE_OK } from "./qa-score-colors";
import { AllTimeChip, ScopedPanel } from "./scoped-panel";
import { ScoreSparkline } from "./score-sparkline";
import {
  BigValue,
  Card,
  MetaLabel,
  SectionTitle,
  SummaryGrid,
} from "./stat-primitives";
import { StatsFilterBar } from "./stats-filter-bar";
import { formatRangeLabel, type StatsFilter } from "./stats-time-filter";
import type { ExamTypeStats, PerformerStats } from "./use-performer-stats";

// ─── Summary Cards ────────────────────────────────────────────────────────────

const OverallQaCard: FC<{ score: string; trend: number[] }> = ({
  score,
  trend,
}) => (
  <Card>
    <MetaLabel>Overall QA Score</MetaLabel>
    <Box
      display="flex"
      alignItems="baseline"
      style={{ gap: 8, marginBottom: 8 }}
    >
      <BigValue style={{ color: getScoreColor(score) }}>{score}</BigValue>
      {score !== "—" && (
        <Typography style={{ fontSize: rem(14), color: EPOCUS_TEXT_SECONDARY }}>
          / 5
        </Typography>
      )}
    </Box>
    <ScoreSparkline data={trend} color={SCORE_GOOD} />
  </Card>
);

const VolumeCard: FC<{ volume: number; trend: number[] }> = ({
  volume,
  trend,
}) => (
  <Card>
    <MetaLabel>Volume</MetaLabel>
    <Box
      display="flex"
      alignItems="baseline"
      style={{ gap: 8, marginBottom: 8 }}
    >
      <BigValue>{volume}</BigValue>
      <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
        encounters
      </Typography>
    </Box>
    <ScoreSparkline data={trend} color="#3B82F6" />
  </Card>
);

const TopFeedbackCard: FC<{
  tags: Array<{ tag: string; pct: number }>;
}> = ({ tags }) => (
  <Card>
    <MetaLabel>Top Feedback</MetaLabel>
    {tags.length === 0 ? (
      <Typography
        style={{
          fontSize: rem(13),
          color: EPOCUS_TEXT_SECONDARY,
          marginTop: 8,
        }}
      >
        No reviews in this range
      </Typography>
    ) : (
      <Box
        display="flex"
        flexDirection="column"
        style={{ gap: 10, marginTop: 4 }}
      >
        {tags.map(({ tag, pct }) => (
          <Box
            key={tag}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              style={{ fontSize: rem(13), color: EPOCUS_TEXT_PRIMARY }}
            >
              {tag}
            </Typography>
            <Typography
              style={{
                fontSize: rem(13),
                fontWeight: 700,
                color: pct >= 50 ? SCORE_GOOD : SCORE_OK,
              }}
            >
              {pct}%
            </Typography>
          </Box>
        ))}
      </Box>
    )}
  </Card>
);

// ─── QA Scores by Exam Type ───────────────────────────────────────────────────

const ExamTypeGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 10,
  [epocusTheme.breakpoints.up("sm")]: {
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  },
});

const ExamTypeCard: FC<{ stats: ExamTypeStats }> = ({ stats }) => {
  const { examType, rangeScore, rangeTrend } = stats;
  const color = EXAM_COLORS[examType];
  const ExamIcon = EXAM_ICONS[examType] ?? UltrasoundScanner;

  return (
    <Card style={{ padding: "14px 14px 12px" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        style={{ marginBottom: 6 }}
      >
        <Box display="flex" alignItems="center" style={{ gap: 6 }}>
          <ExamIcon
            width={18}
            height={18}
            fill={EPOCUS_ICON_ACTIVE}
            aria-hidden
          />
          <Typography
            style={{
              fontSize: rem(12),
              fontWeight: 600,
              color: EPOCUS_TEXT_PRIMARY,
            }}
          >
            {EXAM_LABELS[examType]}
          </Typography>
        </Box>
        <Typography
          style={{
            fontSize: rem(18),
            fontWeight: 700,
            color: getScoreColor(rangeScore),
          }}
        >
          {rangeScore}
        </Typography>
      </Box>
      <ScoreSparkline data={rangeTrend} color={color} width={160} height={38} />
    </Card>
  );
};

// ─── Credentialing Progress ───────────────────────────────────────────────────

const CredentialedBadge = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "2px 8px",
  borderRadius: 20,
  fontSize: rem(11),
  fontWeight: 700,
  backgroundColor: EPOCUS_STATUS_REVIEWED_BG,
  color: EPOCUS_STATUS_REVIEWED_TEXT,
});

const CredRow: FC<{ stats: ExamTypeStats; showDivider: boolean }> = ({
  stats,
  showDivider,
}) => {
  const {
    examType,
    allTimeExamCount,
    allTimeScore,
    credRequired,
    credStatus,
    avgScoreMin,
  } = stats;
  const color = EXAM_COLORS[examType];
  const ExamIcon = EXAM_ICONS[examType] ?? UltrasoundScanner;
  const rawPct = credRequired > 0 ? (allTimeExamCount / credRequired) * 100 : 0;
  // Cap at 99% when not yet credentialed to avoid a full bar contradicting an
  // "In Progress" badge (happens when exam count is met but score gate is open)
  const pct = credStatus === "credentialed" ? 100 : Math.min(99, rawPct);

  return (
    <Box
      style={
        showDivider
          ? { borderBottom: `1px solid ${EPOCUS_BORDER}`, paddingBottom: 14 }
          : { paddingBottom: 14 }
      }
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        style={{ marginBottom: 8 }}
      >
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <ExamIcon
            width={18}
            height={18}
            fill={EPOCUS_ICON_ACTIVE}
            aria-hidden
          />
          <Typography
            style={{
              fontSize: rem(13),
              fontWeight: 600,
              color: EPOCUS_TEXT_PRIMARY,
            }}
          >
            {EXAM_LABELS[examType]}
          </Typography>
        </Box>
        {credStatus === "credentialed" ? (
          <CredentialedBadge>✓ Credentialed</CredentialedBadge>
        ) : credStatus === "not_started" ? (
          <Typography
            style={{ fontSize: rem(12), color: EPOCUS_TEXT_SECONDARY }}
          >
            Not started
          </Typography>
        ) : (
          <Typography
            style={{ fontSize: rem(12), color: EPOCUS_TEXT_SECONDARY }}
          >
            {allTimeExamCount} / {credRequired}
            {avgScoreMin > 0 && (
              <span style={{ marginLeft: 4 }}>
                {allTimeScore === "—"
                  ? "· no reviews yet"
                  : `· score ≥${avgScoreMin.toFixed(1)}`}
              </span>
            )}
          </Typography>
        )}
      </Box>
      {/* Progress bar */}
      <Box
        style={{
          position: "relative",
          height: 6,
          borderRadius: 3,
          backgroundColor: EPOCUS_BG,
          overflow: "hidden",
        }}
      >
        <Box
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            height: "100%",
            width: `${pct}%`,
            borderRadius: 3,
            backgroundColor: color,
            transition: "width 0.4s ease",
          }}
        />
      </Box>
    </Box>
  );
};

const CredentialingPanel: FC<{ byExamType: ExamTypeStats[] }> = ({
  byExamType,
}) => (
  <ScopedPanel
    variant="all-time"
    title="Credentialing Progress"
    subtitle="Career totals — the filters above do not apply"
    controls={<AllTimeChip />}
  >
    <Box>
      <Card>
        {byExamType.map((stats, i) => (
          <CredRow
            key={stats.examType}
            stats={stats}
            showDivider={i < byExamType.length - 1}
          />
        ))}
      </Card>
      <Typography
        style={{
          fontSize: rem(11),
          color: EPOCUS_TEXT_SECONDARY,
          marginTop: 8,
          paddingLeft: 2,
        }}
      >
        Requirements:{" "}
        {byExamType
          .map((s) => `${EXAM_LABELS[s.examType]} ${s.credRequired}`)
          .join(" · ")}
        {CREDENTIALING_THRESHOLDS.efast.avgScoreMin > 0
          ? ` · min avg QA score ${CREDENTIALING_THRESHOLDS.efast.avgScoreMin.toFixed(1)} per type`
          : ""}
      </Typography>
    </Box>
  </ScopedPanel>
);

const NoExamsInRange: FC<{ filter: StatsFilter }> = ({ filter }) => {
  const window = formatRangeLabel(filter.range, Date.now());
  return (
    <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
      {window === null
        ? "No exams recorded yet."
        : `No exams between ${window}.`}
    </Typography>
  );
};

// ─── Variant ──────────────────────────────────────────────────────────────────

export const PerformerPerformance: FC<{
  stats: PerformerStats;
  filter: StatsFilter;
  onFilterChange: (change: Partial<StatsFilter>) => void;
  children?: ReactNode;
}> = ({ stats, filter, onFilterChange, children }) => {
  const {
    overallQaScore,
    scoreTrend,
    volume,
    volumeTrend,
    topFeedbackTags,
    byExamType,
    hasExamsInRange,
  } = stats;

  return (
    <>
      <ScopedPanel
        variant="filtered"
        title="Performance"
        subtitle="Everything in this section follows the filters"
        controls={<StatsFilterBar filter={filter} onChange={onFilterChange} />}
      >
        <SummaryGrid>
          <OverallQaCard score={overallQaScore} trend={scoreTrend} />
          <VolumeCard volume={volume} trend={volumeTrend} />
          <TopFeedbackCard tags={topFeedbackTags} />
        </SummaryGrid>

        <Box>
          <SectionTitle>QA Scores by Exam Type</SectionTitle>
          {hasExamsInRange ? (
            <ExamTypeGrid>
              {byExamType.map((stats) => (
                <ExamTypeCard key={stats.examType} stats={stats} />
              ))}
            </ExamTypeGrid>
          ) : (
            <NoExamsInRange filter={filter} />
          )}
        </Box>

        {children}
      </ScopedPanel>

      <CredentialingPanel byExamType={byExamType} />
    </>
  );
};
